import { LocalizedValue } from '@celum/core/model';

/**
 * Removes all HTML tags from the given string and returns a plain text representation.
 *
 * @param html the string containing HTML content to be stripped.
 */
export function stripHtml(html: LocalizedValue): LocalizedValue;
export function stripHtml(html: string): string;
export function stripHtml(html: LocalizedValue | string): LocalizedValue | string {
  if (!html) {
    return '';
  }

  if (typeof html === 'string') {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return doc.body.textContent?.trim() ?? '';
  }

  const result: LocalizedValue = {};
  for (const [key, value] of Object.entries(html)) {
    result[key] = stripHtml(value);
  }
  return result;
}

/**
 * Extracts the text content of the first element within the HTML
 * string that contains at least the specified minimum number of characters.
 *
 * @param html the HTML content to parse and search through.
 * @param minChars the minimum number of characters required in the text. Defaults to 1.
 */
export function firstElementWithText(html: LocalizedValue, minChars?: number): LocalizedValue;
export function firstElementWithText(html: string, minChars?: number): string;
export function firstElementWithText(html: LocalizedValue | string, minChars = 1): LocalizedValue | string {
  if (!html) {
    return '';
  }

  if (typeof html === 'string') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const elements = Array.from(doc.body.children);
    for (const element of elements) {
      const text = stripHtml(element.outerHTML);
      if (text.length >= minChars) {
        return text;
      }
    }
    return '';
  }

  const result: LocalizedValue = {};
  for (const [key, value] of Object.entries(html)) {
    result[key] = firstElementWithText(value, minChars);
  }
  return result;
}
